import { useEffect, useState, useRef } from "react";
import {
  Modal,
  Form,
  Button,
  Tabs,
  Space,
  message
} from "antd";
import LocationPicker from "./LocationPicker";
import ThresholdsTab from "./DeviceModalTabThresholds";
import BasicDeviceInfoTab from "./DeviceModalTabBasicInfo";
import ElementsDeviceInfoTab from "./DeviceModalTabElements";

import useElements from "../hooks/useElements";
import usePeriodicities from "../hooks/usePeriodicities";
import useDeviceElements from "../hooks/useDeviceElements";
import useDeviceElementsForm from "../hooks/useDeviceElementsForm";
import useThresholds from "../hooks/useThresholds";

import { useGeoMapModal, DEFAULT_MAP_POSITION } from "../utils/geoFormUtils";

const defaultPosition = DEFAULT_MAP_POSITION;

export default function DeviceModal({
  open,
  mode,
  form,
  saving,
  selectedDevice,
  onCancel,
  onSave,
}) {
  const { elements: availableElements, loading: loadingElements } = useElements([], false);
  const { periodicities } = usePeriodicities();
  const { deviceElements, loading: loadingDeviceElements } = useDeviceElements(
    open ? selectedDevice?.id : null
  );
  const isVirtual = (mode === "create" ? true : selectedDevice?.entityType?.id === 3);
  const disableElementsTab = mode == "edit";
  const elementsReady = mode !== "edit" || (!loadingElements && !loadingDeviceElements);
  const filteredPeriodicities = isVirtual ? periodicities?.filter(p => p.name?.toLowerCase() !== "avg24h") : periodicities;

  // useDeviceElementsForm({
  //   mode,
  //   elementsReady,
  //   deviceElements,
  //   form,
  // });

  const {
    thresholds: backendThresholds,
    loading: loadingThresholds,
  } = useThresholds(
    mode === "edit" && open ? selectedDevice?.id : null
  );

  const allDataReady =
    mode !== "edit" ||
    (
      !loadingElements &&
      !loadingDeviceElements &&
      !loadingThresholds &&
      availableElements &&
      deviceElements &&
      backendThresholds
    );

  useEffect(() => {
    if (!open || mode !== "edit") return;
    if (!deviceElements || !backendThresholds) return;

    const ep = {};
    const selected = [];

    deviceElements.forEach((de) => {
      const elementId = Number(de.elementId);
      const periodicityId = Number(de.periodicityId);

      if (!ep[elementId]) {
        ep[elementId] = [];
        selected.push(elementId);
      }

      ep[elementId].push(periodicityId);
    });

    form.setFieldsValue({
      name: selectedDevice.name,
      serialNumber: selectedDevice.serialNumber,
      latitude: selectedDevice.latitude,
      longitude: selectedDevice.longitude,
      description: selectedDevice.description,
      entityTypeId: selectedDevice.entityType?.id,
      hasAnemometer: selectedDevice.hasAnemometer ?? false,
      height: selectedDevice.height ?? null,
      elementPeriodicities: ep,
      selectedElements: selected,

      thresholdsDraft: backendThresholds.map((t) => ({
        elementId: Number(t.elementId),
        periodicityId: Number(t.periodicityId),
        minValue: t.minValue,
        maxValue: t.maxValue,
      })),
    });

  }, [
    open,
    mode,
    deviceElements,
    backendThresholds,
    selectedDevice
  ]);

  const [activeTab, setActiveTab] = useState("1");

  const {
    markerPosition,
    showMap,
    expanded,
    mapRefreshKey,
    expandedMapKey,
    setSmallMap,
    setExpandedMap,
    updateMarkerPosition,
    openExpandedMap,
    closeExpandedMap,
  } = useGeoMapModal({
    open,
    lat: selectedDevice?.latitude,
    lng: selectedDevice?.longitude,
    defaultPosition,
  });

  useEffect(() => {
    if (!open) return;

    setActiveTab("1");

    if (mode === "create") {
      const initialValues = {
        name: "",
        serialNumber: "",
        entityTypeId: undefined,
        hasAnemometer: false,
        latitude: defaultPosition[0],
        longitude: defaultPosition[1],
        height: null,
        elementPeriodicities: {},
        thresholdsDraft: [],
        elementId: undefined,
        periodicityId: undefined,
        minValue: null,
        maxValue: null,
      };

      form.resetFields();
      form.setFieldsValue(initialValues);
      updateMarkerPosition([initialValues.latitude, initialValues.longitude], form);
    } else {
      if (selectedDevice?.latitude != null && selectedDevice?.longitude != null) {
        updateMarkerPosition(
          [Number(selectedDevice.latitude), Number(selectedDevice.longitude)],
          form
        );
      }
    }
  }, [open, mode]);

  const handleOk = async () => {
    try {
      await form.validateFields([
        "name",
        "description",
        "latitude",
        "longitude",
        "hasAnemometer",
        "height",
        "elementPeriodicities",
      ]);
      const values = form.getFieldsValue([
        "name",
        "description",
        "latitude",
        "longitude",
        "hasAnemometer",
        "elementPeriodicities",
        "serialNumber",
        "height"
      ]);

      const selected = values.elementPeriodicities || {};
      const elementsArray = Object.entries(selected)
        .filter(([_, periodicities]) => periodicities && periodicities.length > 0)
        .flatMap(([elementId, periodicities]) =>
          periodicities.map((periodicityId) => ({
            elementId: Number(elementId),
            periodicityId: Number(periodicityId),
          }))
        );

      const payload = {
        name: values.name,
        serialNumber: values.serialNumber,
        entityTypeId: mode === "create" ? 3 : selectedDevice?.entityType?.id,
        hasAnemometer: isVirtual ? false : values.hasAnemometer,
        latitude: values.latitude != null ? String(values.latitude) : null,
        longitude: values.longitude != null ? String(values.longitude) : null,
        description: values.description || "",
        elements: elementsArray,
        height: isVirtual ? values.height : null,
        thresholds: form.getFieldValue("thresholdsDraft") || [],
      };

      onSave(payload, form);
    } catch (err) {
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleMapChange = (coords) => updateMarkerPosition(coords, form);
  
  const basicInfoTab = {
    key: "1",
    label: mode === "create" ? "Step 1 - Basic Info" : "Basic Info",
    children: (
      <BasicDeviceInfoTab
        isVirtual={isVirtual}
        mode={mode}
        handleOk={handleOk}
        markerPosition={markerPosition}
        showMap={showMap}
        mapRefreshKey={mapRefreshKey}
        setSmallMap={setSmallMap}
        openExpandedMap={openExpandedMap}
        updateMarkerPosition={updateMarkerPosition}
        handleMapChange={handleMapChange}
      />
    ),
  };

  const elementsTab = {
    key: "2",
    label: mode === "create" ? "Step 2 - Elements" : "Elements",
    children: elementsReady ? (
      <ElementsDeviceInfoTab
        form={form}
        mode={mode}
        isVirtual={isVirtual}
        availableElements={availableElements}
        periodicities={filteredPeriodicities}
        loadingDeviceElements={loadingDeviceElements}
        loadingElements={loadingElements}
        disableElementsTab={disableElementsTab}
      />
    ) : (
      <div style={{ padding: 40, textAlign: "center" }}>
        Loading elements…
      </div>
    ),
  };

  const thresholdsTab = {
    key: "3",
    label: mode === "create" ? "Step 3 - Thresholds" : "Thresholds",
    children: 
      <ThresholdsTab 
        mode={mode} 
        device={selectedDevice} 
        form={form} 
        elements={availableElements}
        periodicities={filteredPeriodicities}
      />,
  };

  const tabItems = [
    basicInfoTab,
    elementsTab,
    thresholdsTab,
  ];

  const handleTabChange = async (nextKey) => {
    if (mode !== "create") {
      setActiveTab(nextKey);
      return;
    }
    if (activeTab === "1" && nextKey === "2") {
      try {
        await form.validateFields([
          "name",
          "description",
          "latitude",
          "longitude",
          ...(isVirtual ? ["height"] : []),
        ]);
        setActiveTab("2");
      } catch {
        return;
      }
    }
    if (activeTab === "2" && nextKey === "3") {
      const elementPeriodicities = form.getFieldValue("elementPeriodicities");

      const hasAny =
        elementPeriodicities &&
        Object.values(elementPeriodicities).some(
          (p) => Array.isArray(p) && p.length > 0
        );

      if (!hasAny) {
        message.warning(
          "Configure at least one element and aggregation before defining thresholds."
        );
        return;
      }

      setActiveTab("3");
      return;
    }

    if (Number(nextKey) < Number(activeTab)) {
      setActiveTab(nextKey);
    }
  };

  return (
    <>
      <Modal
        title={mode === "create" ? "Create Device" : "Edit Device"}
        open={open}
        confirmLoading={saving}
        maskClosable={false}
        onCancel={handleCancel}
        footer={null}
        width={820}
        styles={{
          body: {
            padding: 0,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Form
          form={form}
          layout="vertical"
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
          onValuesChange={(changed, all) => {
            if ("latitude" in changed || "longitude" in changed) {
              const lat = Number(all.latitude);
              const lng = Number(all.longitude);
              if (!isNaN(lat) && !isNaN(lng)) {
                updateMarkerPosition([lat, lng], form);
              }
            }
          }}
        >
          {!allDataReady ? (
            <div style={{ padding: 60, textAlign: "center" }}>
              Loading device configuration...
            </div>
          ) : (
            <>
              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                destroyOnHidden={false}
                style={{ flexShrink: 0 }}
                items={tabItems}
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  padding: "12px 24px",
                  borderTop: "1px solid #f0f0f0",
                  marginTop: "auto",
                }}
              >
                <Space>
                  <Button onClick={handleCancel}>Cancel</Button>

                  {mode === "create" && activeTab === "1" && (
                    <Button type="primary" onClick={() => handleTabChange("2")}>
                      Next
                    </Button>
                  )}

                  {mode === "create" && activeTab === "2" && (
                    <>
                      <Button onClick={() => setActiveTab("1")}>Back</Button>
                      <Button type="primary" onClick={() => handleTabChange("3")}>
                        Next
                      </Button>
                    </>
                  )}

                  {mode === "create" && activeTab === "3" && (
                    <>
                      <Button onClick={() => setActiveTab("2")}>Back</Button>
                      <Button type="primary" loading={saving} onClick={handleOk}>
                        Create
                      </Button>
                    </>
                  )}

                  {mode === "edit" && (
                    <Button type="primary" loading={saving} onClick={handleOk}>
                      Save
                    </Button>
                  )}
                </Space>
              </div>
            </>
          )}
        </Form>
      </Modal>

      {/* Expanded Map Modal */}
      <Modal
        open={expanded}
        onCancel={closeExpandedMap}
        footer={null}
        width={900}
        styles={{ body: { padding: 0 } }}
        centered
        maskClosable={false}
      >
        <div style={{ height: 500, position: "relative" }}>
          <Button
            type="primary"
            size="large"
            style={{ position: "absolute", top: 18, right: 28, zIndex: 1200 }}
            onClick={closeExpandedMap}
          >
            Select
          </Button>
          <LocationPicker
            value={markerPosition}
            onChange={handleMapChange}
            disabled={false}
            setContainerMap={setExpandedMap}
            forceRefresh={expandedMapKey}
          />
        </div>
      </Modal>
    </>
  );
}
